import {
    Observable,
    map
} from "rxjs";

import {
    inject,
    Injectable
} from "@angular/core";

import {
    DataEntryDeal, DataEntryDealRequestOptions, DataEntryDealSearchFilterOptions
} from "@shopliftr/common-js/admin";
import {
    ModelAction,
    StringUtil, SearchSortDirection, ISearchSort
} from "@shopliftr/common-js/data";
import {
    BulkResponse,
    BulkUtil,
    CreateRequest,
    CreateResponse,
    DeleteRequest,
    DeleteResponse,
    GetResponse,
    RequestUtil,
    SearchRequest,
    SearchResponse,
    UpdateRequest,
    UpdateResponse,
} from "@shopliftr/common-js/shared";

import {
    InternalApiRequestService,
    IFullAccessInternalApiClientService,
    ServiceException,
    ListUtil
} from "@shopliftr/common-ng";

/**
 * Leverages the DataEntryDeal endpoint to retrieve and manipulate data entry deals.
 *
 * **Service Pattern:** API Client Service
 *
 * **Provision:** Singleton service provided in 'root'
 *
 * @implements {IFullAccessInternalApiClientService<DataEntryDeal>} Standard internal API full-access client pattern
 */
@Injectable({ providedIn: "root" })
export class DataEntryDealApiClientService implements IFullAccessInternalApiClientService<DataEntryDeal> {

    readonly basePath = "data-entry-deal";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    private _searchCount = 0;

    bulkDelete$(items: Array<DeleteRequest<DataEntryDealRequestOptions>>): Observable<BulkResponse> {

        if (!items?.length) {
            throw new ServiceException("Could not perform a bulk operation with no requests");
        }

        const bulkRequest = BulkUtil.createBulkRequest(items.map((request: DeleteRequest<DataEntryDealRequestOptions>) => {

            return BulkUtil.createRequestItem(request);
        }));

        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    bulkUpdate$(
        requests: Array<UpdateRequest<DataEntryDeal, DataEntryDealRequestOptions>>
    ): Observable<BulkResponse> {

        if (!requests?.length) {
            throw new ServiceException("Could not perform a bulk operation with no requests");
        }

        const bulkRequest = BulkUtil.createBulkRequest(requests.map(
            (request: UpdateRequest<DataEntryDeal, DataEntryDealRequestOptions>) => {
                this._sanitizeLocalizations(request.fields);
                return BulkUtil.createRequestItem(request);
            }
        ));

        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<DataEntryDeal>(id, DataEntryDeal.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Creates several deals in one bulk request, sanitizing each deal's localizations first.
     */
    bulkCreate$(requests: Array<CreateRequest<DataEntryDeal, DataEntryDealRequestOptions>>): Observable<BulkResponse> {

        if (!requests?.length) {
            throw new ServiceException("Could not perform a bulk operation with no requests");
        }

        const bulkRequest = BulkUtil.createBulkRequest(requests.map(
            (request: CreateRequest<DataEntryDeal, DataEntryDealRequestOptions>) => {
                this._sanitizeLocalizations(request.fields);
                return BulkUtil.createRequestItem(request);
            }
        ));

        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    create$(deal: DataEntryDeal, options: DataEntryDealRequestOptions): Observable<DataEntryDeal> {

        if (!options) {
            throw new ServiceException("Could not create data entry deal: options are required, but were not provided");
        }

        const request: CreateRequest<DataEntryDeal, DataEntryDealRequestOptions> = RequestUtil.createCreateRequest(
            ModelAction.ClientCreate,
            deal,
            options
        );

        this._sanitizeLocalizations(request.fields);

        return this._internalApiRequestService.create$(
            request,
            this.basePath
        ).pipe(map((response: CreateResponse<DataEntryDeal>) => response.payload));
    }

    delete$(id: string, version: number, options: DataEntryDealRequestOptions): Observable<DeleteResponse> {

        if (!options) {
            throw new ServiceException("Could not delete data entry deal: options are required, but were not provided");
        }

        const request = RequestUtil.createDeleteRequest(id, DataEntryDeal.modelName, ModelAction.ClientDelete, version, options);

        return this._internalApiRequestService.delete$(request, this.basePath);
    }

    get$(id: string): Observable<DataEntryDeal> {

        const request = RequestUtil.createGetRequest<void>(id, DataEntryDeal.modelName, ModelAction.ClientGet);

        return this._internalApiRequestService.get$(request, this.basePath)
            .pipe(map((response: GetResponse<DataEntryDeal>) => response.payload));
    }

    getAll$(filterOptions: DataEntryDealSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<DataEntryDeal>> {

        this._searchCount++;

        const resolvedSort: Array<ISearchSort> = sort ?? [{
            field: "name",
            direction: SearchSortDirection.Ascending
        }];

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            DataEntryDeal.modelName,
            undefined,
            undefined,
            filterOptions,
            resolvedSort
        );
        return this._internalApiRequestService.getAll$(request, this.basePath);
    }

    search$(
        filterOptions: DataEntryDealSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<DataEntryDeal>> {

        this._searchCount++;

        const request: SearchRequest<DataEntryDealSearchFilterOptions> = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            DataEntryDeal.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$(request, this.basePath);
    }

    update$(
        id: string,
        version: number,
        fields: DataEntryDeal,
        removeFields: Array<string> | undefined,
        options: DataEntryDealRequestOptions
    ): Observable<DataEntryDeal> {

        if (!options) {
            throw new ServiceException("Could not update data entry deal: options are required, but were not provided");
        }

        const request = RequestUtil.createUpdateRequest(id, ModelAction.ClientUpdate, fields, version, removeFields, options);
        this._sanitizeLocalizations(request.fields);
        return this._internalApiRequestService.update$(request, this.basePath).pipe(map(
            (response: UpdateResponse<DataEntryDeal>) => response.payload
        ));
    }

    /**
     * Updates the deal with the changes computed between the original and updated models.
     * The updated model is sanitized on a clone, so the caller's instance is left untouched.
     */
    updateWithChanges$(
        original: DataEntryDeal,
        updated: DataEntryDeal,
        options: DataEntryDealRequestOptions
    ): Observable<DataEntryDeal> {

        if (!options) {
            throw new ServiceException("Could not update data entry deal: options are required, but were not provided");
        }

        const sanitized = updated.clone();
        this._sanitizeLocalizations(sanitized);
        return this._internalApiRequestService.updateWithChanges$(original, sanitized, this.basePath, options);
    }

    /**
     * Returns the total number of deals for the flyer.
     *
     * Backs the deal entry drift check, which compares this total against the cached deal count.
     */
    getCount$(flyerId: string): Observable<number> {

        const filterOptions = new DataEntryDealSearchFilterOptions();
        filterOptions.setDefaults();
        filterOptions.flyerId = flyerId;
        return this.search$(filterOptions, 0, 0).pipe(map((value) => value.total));
    }

    /**
     * Returns true if a deal has been entered on the flyer for the given upc
     */
    dealExistsForUpc$(flyerId: string, upc: string): Observable<boolean> {

        const filterOptions = new DataEntryDealSearchFilterOptions();
        filterOptions.setDefaults();
        filterOptions.flyerId = flyerId;
        filterOptions.upcs = [upc];
        return this.search$(filterOptions, 0, 0).pipe(map((value) => value.total > 0));
    }

    /**
     * Returns the number of `search$`/`getAll$` calls since the last call to this method, then resets the count.
     * Read by deal entry after each save to confirm no searches were made between saves.
     *
     * Intended to have exactly two readers, both in the deal list state: the `DealSaved` page action (the count
     * since the last save), and the deal cache seed, which reads it only to discard the seed request's own search so
     * the first `DealSaved` after a seed does not report it. Any other caller resets the count and breaks that check.
     */
    takeSearchCount(): number {
        const count = this._searchCount;
        this._searchCount = 0;
        return count;
    }

    /**
     * Remove empty strings from the localizations
     */
    private _sanitizeLocalizations(fields: DataEntryDeal): void {

        delete fields.additional;
        delete fields.additionalArray;
        if (fields?.localizations) {
            for (const localization of fields.localizations) {
                for (const fieldName in localization) {
                    if (StringUtil.isString(fields[fieldName]) && StringUtil.emptyString(fields[fieldName] as string)) {
                        delete localization[fieldName];
                    }
                }
            }
        }
    }
}
